/**
 * Register all store's from @/modules.
 * Within @/modules/moduleName/store there should be and index.js for the base store
 * and related stores expand out with [sub-]directory hierarchy.
 * modules are namespaced with the camelCase equivalent of their file name.
 *
 * Given the following example structure
 * customers/
 * ├── store/
 * │   ├── components/
 * │   │     └── payment-instrument.js
 * │   ├── index.js
 * │   └── test.js
 * └── customer-details.vue
 *
 * Will produce the following namespaced Stores
 * 'customers', 'customersTest', 'customersComponents/paymentInstrument'
 */

import camelCase from '@/common/helpers/camel-case';

export default class StoreModules {
    constructor({apiHandler, context, protectedNamespaces = []}) {
        this.apiHandler = apiHandler;
        this.context = context;
        this.protectedNamespaces = protectedNamespaces;
        this.modulesCache = {};
        this.data = {modules: {}};

        this.updateModules();
    }

    get modules() {
        return this.data.modules;
    }

    // Recursively get the namespace of a Vuex module, even if nested.
    getNamespace(subtree, path) {
        if (path.length === 1) return subtree;

        const namespace = path.shift();
        subtree.modules[namespace] = {
            modules: {},
            namespaced: true,
            ...subtree.modules[namespace],
        };
        return this.getNamespace(subtree.modules[namespace], path);
    }

    generateStoreModule(fileName, moduleDefinition) {
        // Skip the module during hot reload if it refers to the
        // same module definition as the one we have cached.
        if (this.modulesCache[fileName] === moduleDefinition) return;
        // Update the module cache, for efficient hot reloading.
        this.modulesCache[fileName] = moduleDefinition;

        // Get the module path as an array.
        // Remove the "./" from the beginning.
        let modulePath = fileName.replace(/^\.\//, '');
        if (/\/index\.js$/i.test(modulePath)) {
            // Remove "/store/index" from index.js files to name them moduleName.js
            modulePath = modulePath.replace(/\/store\/index/, '');
        } else {
            // replace parent folder "/store/" with "-" to convert file name to kebab-case
            modulePath = modulePath.replace(/\/store\//, '-');
        }
        // Remove the file extension from the end.
        modulePath = modulePath.replace(/\.\w+$/, '')
        // Split nested modules into an array path.
        // eslint-disable-next-line no-useless-escape
            .split(/\//)
            // camelCase all module namespaces and names.
            .map(camelCase);

        if (this.protectedNamespaces.includes(modulePath[0])) {
            throw new Error(`store module name "${modulePath[0]}" already exists, please use a different name or refactor the existing store`);
        }

        // Get the modules object for the current path.
        const {modules} = this.getNamespace(this.data, modulePath);

        // Add the module to our modules object.
        const modulePathPop = modulePath.pop();
        modules[modulePathPop] = {
            // Modules are namespaced by default.
            namespaced: true,
            ...moduleDefinition,
        };

        if (typeof moduleDefinition.actions === 'function') {
            modules[modulePathPop] = {
                ...modules[modulePathPop],
                actions: moduleDefinition.actions(this.apiHandler),
            };
        }

        if (typeof moduleDefinition.dependencies === 'function') {
            moduleDefinition.dependencies();
        }
    }

    updateModules() {
        // For every module...
        this.context.modules.keys().forEach((fileName) => {
            const moduleDefinition = this.context.modules(fileName);
            this.generateStoreModule(fileName, moduleDefinition);
        });

        // For every component...
        this.context.components.keys().forEach((fileName) => {
            const moduleDefinition = this.context.components(fileName);
            this.generateStoreModule(fileName, moduleDefinition);
        });

        // If the environment supports hot reloading...
        if (module.hot) {
            // Whenever any Vuex module is updated...
            module.hot.accept([this.context.modules.id, this.context.components.id], () => {
                // Update `data.modules` with the latest definitions.
                this.updateModules();
                // Trigger a hot update in the store.
                // eslint-disable-next-line global-require
                require('@/store/index').default.hotUpdate({modules: this.data.modules});
            });
        }
    }
}
